import React, { useState, useRef } from "react";
import Webcam from "react-webcam";

/* ================= CONFIG ================= */

const API_URL = "https://neusoftbackend.onrender.com/api";
const LOGO_URL =
  "https://www.neusoft.com/wp-content/themes/neusoft-en/assets/img/logo.svg";

/* ================= UI COMPONENTS ================= */

const Navbar = ({ user }) => (
  <nav className="sticky top-0 z-50 bg-white border-b border-gray-200">
    <div className="max-w-7xl mx-auto px-8 py-5 flex justify-between items-center">
      <div className="flex items-center gap-4">
        <img src={LOGO_URL} alt="Logo" className="h-7" />
        <span className="text-xs tracking-[0.35em] font-semibold text-gray-700">
          SECURE EXAMINATION PORTAL
        </span>
      </div>
      {user && (
        <div className="text-xs text-gray-500 tracking-widest">
          CANDIDATE ID: <span className="font-semibold">{user.username}</span>
        </div>
      )}
    </div>
  </nav>
);

const SectionCard = ({ title, children }) => (
  <div className="border border-gray-200 rounded-lg p-6 bg-white shadow-sm">
    <h3 className="text-sm font-semibold tracking-widest text-gray-800 mb-4 uppercase">
      {title}
    </h3>
    <div className="text-xs text-gray-600 leading-relaxed space-y-2">
      {children}
    </div>
  </div>
);

const MicVisualizer = () => (
  <div className="flex items-end gap-[2px] h-3">
    {[...Array(6)].map((_, i) => (
      <div
        key={i}
        className="w-[2px] bg-white animate-pulse"
        style={{ height: `${30 + Math.random() * 60}%` }}
      />
    ))}
  </div>
);

/* ================= APP ================= */

export default function App() {
  const [page, setPage] = useState("login");
  const [isLogin, setIsLogin] = useState(true);
  const [user, setUser] = useState(null);

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
  });

  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [examResult, setExamResult] = useState(null);

  const [hasPermissions, setHasPermissions] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState("");

  const webcamRef = useRef(null);

  /* ================= PERMISSIONS ================= */

  const requestPermissions = () => {
    navigator.mediaDevices
      .getUserMedia({ video: true, audio: true })
      .then((stream) => {
        stream.getTracks().forEach((t) => t.stop());
        setHasPermissions(true);
      })
      .catch((err) => {
        alert("Permission error: " + err.name);
      });
  };

  /* ================= AUTH ================= */

  const handleAuth = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const endpoint = isLogin ? "/login" : "/register";
      const res = await fetch(API_URL + endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          isLogin
            ? { email: formData.email, password: formData.password }
            : formData
        ),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      setUser(data.user);
      setPage("instructions");
    } catch (e) {
      setError(e.message);
    }
  };

  /* ================= EXAM ================= */

  const startExam = async () => {
    const res = await fetch(`${API_URL}/questions`);
    const data = await res.json();
    setQuestions(data);
    setPage("exam");
  };

  const submitExam = async () => {
    const payload = Object.entries(answers).map(([id, opt]) => ({
      questionId: Number(id),
      selectedOption: opt,
    }));

    const res = await fetch(`${API_URL}/submit-exam`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        userId: user.id,
        username: user.username,
        answers: payload,
      }),
    });

    const data = await res.json();
    setExamResult(data);
    setPage("results");
  };

  /* ================= LOGIN ================= */

  if (page === "login") {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-[85vh]">
          <form
            onSubmit={handleAuth}
            className="bg-white border border-gray-200 rounded-xl shadow-md p-10 w-[360px] space-y-6"
          >
            <h2 className="text-center text-sm tracking-widest font-semibold">
              CANDIDATE AUTHENTICATION
            </h2>

            {!isLogin && (
              <input
                placeholder="FULL NAME"
                required
                onChange={(e) =>
                  setFormData({ ...formData, username: e.target.value })
                }
                className="w-full border-b text-xs py-2 outline-none"
              />
            )}

            <input
              placeholder="EMAIL ADDRESS"
              required
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              className="w-full border-b text-xs py-2 outline-none"
            />

            <input
              type="password"
              placeholder="PASSWORD"
              required
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              className="w-full border-b text-xs py-2 outline-none"
            />

            {error && (
              <p className="text-red-600 text-[10px] text-center">{error}</p>
            )}

            <button className="w-full bg-black text-white py-3 text-xs tracking-widest rounded-md">
              CONTINUE
            </button>

            <p
              className="text-[10px] text-center underline cursor-pointer"
              onClick={() => setIsLogin(!isLogin)}
            >
              {isLogin ? "Create new account" : "Back to login"}
            </p>
          </form>
        </div>
      </div>
    );
  }

  /* ================= INSTRUCTIONS ================= */

  if (page === "instructions") {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} />
        <div className="max-w-6xl mx-auto px-8 py-16 grid grid-cols-1 md:grid-cols-2 gap-8">
          <SectionCard title="Exam Instructions">
            <p>• Total duration is strictly timed.</p>
            <p>• Do not refresh, minimize, or switch tabs.</p>
            <p>• Each question has only one correct answer.</p>
            <p>• Once submitted, answers cannot be changed.</p>
          </SectionCard>

          <SectionCard title="Proctoring & Monitoring">
            <p>• Webcam and microphone must remain active.</p>
            <p>• Face must be visible at all times.</p>
            <p>• Background noise or multiple faces will disqualify.</p>
            <p>• Screen activity is continuously monitored.</p>
          </SectionCard>

          <SectionCard title="Terms & Conditions">
            <p>
              By proceeding, you confirm that you will attempt this examination
              independently without external assistance.
            </p>
            <p>
              Any attempt to cheat, impersonate, or tamper with the system will
              result in permanent disqualification.
            </p>
          </SectionCard>

          <SectionCard title="System Check">
            <p>• Stable internet connection required.</p>
            <p>• Use a desktop or laptop only.</p>
            <p>• Close all background applications.</p>
          </SectionCard>
        </div>

        <div className="max-w-3xl mx-auto pb-16 px-8">
          <div className="flex items-start gap-3 mb-6">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={() => setAcceptedTerms(!acceptedTerms)}
            />
            <p className="text-xs text-gray-600">
              I have read and agree to all instructions and terms stated above.
            </p>
          </div>

          {!hasPermissions ? (
            <button
              disabled={!acceptedTerms}
              onClick={requestPermissions}
              className="w-full border border-black py-4 text-xs tracking-widest disabled:opacity-40"
            >
              INITIALIZE CAMERA & MICROPHONE
            </button>
          ) : (
            <button
              onClick={startExam}
              className="w-full bg-black text-white py-4 text-xs tracking-widest"
            >
              START EXAMINATION
            </button>
          )}
        </div>
      </div>
    );
  }

  /* ================= EXAM ================= */

  if (page === "exam") {
    const q = questions[currentQuestion];

    return (
      <div className="min-h-screen bg-white">
        <Navbar user={user} />

        <div className="fixed top-24 right-8 w-56 z-50">
          <div className="border border-gray-300 rounded-md overflow-hidden">
            {hasPermissions && (
              <Webcam
                ref={webcamRef}
                audio
                muted
                videoConstraints={{ facingMode: "user" }}
              />
            )}
          </div>
          <div className="bg-black text-white px-3 py-2 flex justify-between text-[10px]">
            <span>LIVE PROCTORING</span>
            <MicVisualizer />
          </div>
        </div>

        <main className="max-w-4xl mx-auto px-8 py-20">
          <p className="text-xs text-gray-500 mb-3">
            Question {currentQuestion + 1} of {questions.length}
          </p>
          <h2 className="text-lg font-medium mb-10">{q?.question}</h2>

          <div className="space-y-4">
            {q?.options.map((opt, i) => (
              <button
                key={i}
                onClick={() => setAnswers({ ...answers, [q.id]: i })}
                className={`w-full text-left p-4 border rounded-md text-sm ${
                  answers[q.id] === i
                    ? "border-black bg-gray-900 text-white"
                    : "border-gray-300 hover:border-black"
                }`}
              >
                {opt}
              </button>
            ))}
          </div>

          <div className="flex justify-between mt-16">
            <button
              disabled={currentQuestion === 0}
              onClick={() => setCurrentQuestion((c) => c - 1)}
              className="text-xs underline disabled:opacity-30"
            >
              Previous
            </button>

            {currentQuestion === questions.length - 1 ? (
              <button
                onClick={submitExam}
                className="bg-black text-white px-8 py-3 text-xs rounded-md"
              >
                Submit Exam
              </button>
            ) : (
              <button
                onClick={() => setCurrentQuestion((c) => c + 1)}
                className="bg-black text-white px-8 py-3 text-xs rounded-md"
              >
                Next
              </button>
            )}
          </div>
        </main>
      </div>
    );
  }

  /* ================= RESULTS ================= */

  if (page === "results") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white border border-gray-200 shadow-lg rounded-xl p-16 text-center">
          <p className="text-xs tracking-widest text-gray-500 mb-4">
            EXAMINATION RESULT
          </p>
          <div className="text-6xl font-light mb-6">
            {Math.round(examResult?.percentage)}%
          </div>
          <p className="text-sm mb-10">
            {examResult?.percentage >= 50
              ? "STATUS: PASSED"
              : "STATUS: FAILED"}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-black text-white px-12 py-4 text-xs tracking-widest rounded-md"
          >
            EXIT PORTAL
          </button>
        </div>
      </div>
    );
  }

  return null;
}
